import Swal from 'sweetalert2';
import type { AdminPost } from '@/mocks/adminPosts';
import { postTypeLabels, postTypeIcons, postTypeColors } from '@/mocks/adminPosts';
import { Link } from 'react-router-dom';

interface PostCardProps {
  post: AdminPost;
  onEdit: (post: AdminPost) => void;
  onDelete: (post: AdminPost) => void;
  onToggleFeatured: (post: AdminPost) => void;
  onPublishNow: (post: AdminPost) => void;
  onShare: (post: AdminPost) => void;
  onViewResults: (post: AdminPost) => void;
  onClosePoll: (post: AdminPost) => void;
}

function formatRelativeTime(iso: string): string {
  const now = new Date();
  const d = new Date(iso);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHrs < 24) return `Hace ${diffHrs} h`;
  if (diffDays < 7) return `Hace ${diffDays} d`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

const RADIO_NAME = 'Radio Nueva Actitud';

export default function PostCard({ post, onEdit, onDelete, onToggleFeatured, onPublishNow, onShare, onViewResults, onClosePoll }: PostCardProps) {
  const canPublishNow = post.status === 'borrador' || post.status === 'programado';
  const isPoll = post.type === 'encuesta' && post.pollData;
  const pollTotalVotes = post.pollData?.options.reduce((sum, o) => sum + o.votes, 0) || 0;
  const displayTime = post.status === 'programado' && post.scheduledDate ? post.scheduledDate : post.createdAt;

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-3.5 pt-3.5 pb-2.5">
        <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center shrink-0 overflow-hidden">
          <span className="text-primary-700 font-bold text-xs">RNA</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold text-foreground-900">{RADIO_NAME}</span>
            {post.isFeatured && <i className="ri-star-fill text-amber-500 text-[10px]"></i>}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-foreground-400">{formatRelativeTime(displayTime)}</span>
            <span className="text-foreground-300">·</span>
            <i className="ri-earth-line text-[10px] text-foreground-400"></i>
            {post.isSponsored && (
              <>
                <span className="text-foreground-300">·</span>
                <span className="text-[10px] text-foreground-400">Patrocinado</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Type + Status badges ── */}
      <div className="px-3.5 pb-2 flex items-center gap-1.5 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${postTypeColors[post.type]}`}>
          <i className={`${postTypeIcons[post.type]} text-[9px]`}></i>
          {postTypeLabels[post.type]}
        </span>
        {post.derivedFromLiveId && (
          <Link to="/admin/live" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 hover:bg-red-200 cursor-pointer transition-colors">
            <i className="ri-live-line text-[9px]"></i>
            Live #{post.derivedFromLiveId}
          </Link>
        )}
        {post.derivedFromVideoId && (
          <Link to="/admin/videos" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer transition-colors">
            <i className="ri-video-line text-[9px]"></i>
            Video #{post.derivedFromVideoId}
          </Link>
        )}
        {post.status === 'programado' && post.scheduledDate && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
            <i className="ri-time-line text-[9px]"></i>
            {new Date(post.scheduledDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
          </span>
        )}
        {post.status === 'borrador' && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-foreground-100 text-foreground-500">
            <i className="ri-draft-line text-[9px]"></i>
            Borrador
          </span>
        )}
      </div>

      {/* ── Sponsored Label ── */}
      {post.isSponsored && post.sponsorName && (
        <div className="px-3.5 pb-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-50 text-primary-700 text-[10px] font-semibold">
            <i className="ri-megaphone-line text-[9px]"></i>
            Patrocinado · {post.sponsorName}
          </span>
        </div>
      )}

      {/* ── Text ── */}
      {post.text && (
        <div className="px-3.5 pb-2.5">
          <p className="text-sm text-foreground-800 leading-relaxed whitespace-pre-line">{post.text}</p>
        </div>
      )}

      {/* ── Image ── */}
      {post.imageUrl && (['imagen', 'patrocinada'].includes(post.type) || post.type === 'video') && (
        <div className={`relative w-full ${post.type === 'video' ? 'group cursor-pointer' : ''}`}>
          <img src={post.imageUrl} alt={post.title} className="w-full object-cover object-top" style={{ maxHeight: '320px' }} />
          {post.type === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-foreground-950/60 flex items-center justify-center">
                <i className="ri-play-fill text-background-50 text-xl ml-0.5"></i>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Poll ── */}
      {isPoll && post.pollData && (
        <div className="px-3.5 pb-2.5">
          <div className="p-3 rounded-xl bg-amber-50/60 border border-amber-200 space-y-2">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                post.pollData.isClosed ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {post.pollData.isClosed ? 'Cerrada' : 'Abierta'}
              </span>
              <span className="text-[10px] text-foreground-400">{pollTotalVotes.toLocaleString()} votos</span>
            </div>
            {post.pollData.options.slice(0, 3).map((opt) => {
              const pct = pollTotalVotes > 0 ? Math.round((opt.votes / pollTotalVotes) * 100) : 0;
              return (
                <div key={opt.id} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-background-50 border border-background-200">
                  <span className="text-[11px] text-foreground-700 truncate flex-1">{opt.text}</span>
                  <span className="text-[11px] font-bold text-foreground-500 ml-2">{pct}%</span>
                </div>
              );
            })}
            {post.pollData.options.length > 3 && (
              <p className="text-[10px] text-foreground-400 text-center">+{post.pollData.options.length - 3} opciones más</p>
            )}
          </div>
        </div>
      )}

      {/* ── Engagement ── */}
      {post.status === 'publicado' && (
        <div className="px-3.5 pb-2">
          {(post.engagement.likes > 0 || post.engagement.comments > 0 || post.engagement.shares > 0) && (
            <div className="flex items-center justify-between pb-2 border-b border-background-100">
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-thumb-up-fill text-primary-600 text-[8px]"></i>
                </span>
                <span className="text-[11px] text-foreground-400">{formatCount(post.engagement.likes)}</span>
              </div>
              <div className="flex items-center gap-2">
                {post.engagement.comments > 0 && <span className="text-[11px] text-foreground-400">{formatCount(post.engagement.comments)} com.</span>}
                {post.engagement.shares > 0 && <span className="text-[11px] text-foreground-400">{formatCount(post.engagement.shares)} comp.</span>}
              </div>
            </div>
          )}
          <div className="flex items-center pt-1.5">
            <button onClick={() => Swal.fire({ toast: true, position: 'top-end', title: '¡Te gusta esta publicación!', icon: 'success', showConfirmButton: false, timer: 1500, timerProgressBar: true, customClass: { popup: 'rounded-xl' } })} className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] font-semibold text-foreground-400 cursor-pointer hover:bg-background-100 rounded-lg transition-colors">
              <i className="ri-thumb-up-line text-sm"></i> Me gusta
            </button>
            <button onClick={() => Swal.fire({ toast: true, position: 'top-end', title: 'Comentarios en la página pública', icon: 'info', showConfirmButton: false, timer: 1500, timerProgressBar: true, customClass: { popup: 'rounded-xl' } })} className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] font-semibold text-foreground-400 cursor-pointer hover:bg-background-100 rounded-lg transition-colors">
              <i className="ri-chat-3-line text-sm"></i> Comentar
            </button>
            <button onClick={() => onShare(post)} className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] font-semibold text-foreground-400">
              <i className="ri-share-forward-line text-sm"></i> Compartir
            </button>
          </div>
        </div>
      )}

      {/* ── Admin Actions ── */}
      <div className="px-3.5 pb-3.5 pt-1 border-t border-background-100 flex items-center gap-1.5 flex-wrap">
        <button onClick={() => onEdit(post)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-500 hover:bg-background-100 cursor-pointer transition-colors">
          <i className="ri-pencil-line text-xs"></i> Editar
        </button>
        <button onClick={() => onToggleFeatured(post)} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-colors ${post.isFeatured ? 'text-amber-600 bg-amber-50' : 'text-foreground-500 hover:bg-background-100'}`}>
          <i className={post.isFeatured ? 'ri-star-fill' : 'ri-star-line'}></i>
          {post.isFeatured ? 'Destacada' : 'Destacar'}
        </button>
        {canPublishNow && (
          <button onClick={() => onPublishNow(post)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-emerald-600 hover:bg-emerald-50 cursor-pointer transition-colors">
            <i className="ri-send-plane-fill text-xs"></i> Publicar
          </button>
        )}
        {isPoll && (
          <button onClick={() => onViewResults(post)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-amber-600 hover:bg-amber-50 cursor-pointer transition-colors">
            <i className="ri-bar-chart-2-line text-xs"></i> Resultados
          </button>
        )}
        <button onClick={() => onDelete(post)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-red-500 hover:bg-red-50 cursor-pointer transition-colors ml-auto">
          <i className="ri-delete-bin-line text-xs"></i> Eliminar
        </button>
      </div>
    </div>
  );
}